import React, { useEffect, useState } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function PlaybookEditor() {
  const [activeTab, setActiveTab] = useState('global');

  // Global Rules State
  const [globalText, setGlobalText] = useState('');
  const [originalGlobalText, setOriginalGlobalText] = useState('');
  const [isSavingGlobal, setIsSavingGlobal] = useState(false);

  // Parents and Sub-Folders State
  const [parents, setParents] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderTexts, setFolderTexts] = useState({});
  const [originalFolderTexts, setOriginalFolderTexts] = useState({});
  const [collapsedFolders, setCollapsedFolders] = useState({});

  // Inline Editing State
  const [editingParentId, setEditingParentId] = useState(null);
  const [editingParentName, setEditingParentName] = useState('');

  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  // Creation Form State
  const [newParentName, setNewParentName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedParent, setSelectedParent] = useState('');

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db) return;

    // 1. Global Rules Listener
    const unsubGlobal = onSnapshot(doc(db, 'commercialPlaybook', 'global'), (snap) => {
      if (snap.exists() && snap.data().rules) {
        const text = snap.data().rules.join('\n');
        setGlobalText(text);
        setOriginalGlobalText(text);
      }
    });

    // 2. Parent Folders Listener
    const unsubParents = onSnapshot(collection(db, 'playbookParents'), (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.priority || 0) - (b.priority || 0));

      // Seed default 'general' parent if collection is empty
      if (items.length === 0) {
        setDoc(doc(db, 'playbookParents', 'general'), {
          name: 'General Platform Rules',
          priority: 1
        });
      }

      setParents(items);
      if (items.length > 0 && !selectedParent) {
        setSelectedParent(items[0].id);
      }
    });

    // 3. Sub-Folders Listener
    const unsubFolders = onSnapshot(collection(db, 'playbookFolders'), (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.priority || 0) - (b.priority || 0));
      setFolders(items);

      const textMap = {};
      const initialCollapse = {};
      items.forEach((f) => {
        const text = (f.rules || []).join('\n');
        textMap[f.id] = text;
        initialCollapse[f.id] = true;
      });

      setFolderTexts(textMap);
      setOriginalFolderTexts(textMap);
      setCollapsedFolders((prev) => ({ ...initialCollapse, ...prev }));
      setLoading(false);
    });

    return () => {
      unsubGlobal();
      unsubParents();
      unsubFolders();
    };
  }, [selectedParent]);

  // Dirty State Helpers
  const isGlobalDirty = globalText !== originalGlobalText;
  const isFolderDirty = (folderId) => folderTexts[folderId] !== originalFolderTexts[folderId];

  const toggleCollapse = (folderId) => {
    setCollapsedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  // --- GLOBAL RULES ---
  const handleSaveGlobal = async (e) => {
    e.preventDefault();
    setIsSavingGlobal(true);
    try {
      const rulesArray = globalText
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      await setDoc(doc(db, 'commercialPlaybook', 'global'), { rules: rulesArray });
      setOriginalGlobalText(globalText);
    } catch (err) {
      console.error('Failed to save global rules:', err);
    } finally {
      setIsSavingGlobal(false);
    }
  };

  // --- PARENT FOLDER ACTIONS ---
  const handleCreateParent = async (e) => {
    e.preventDefault();
    if (!newParentName.trim()) return;

    const parentId = newParentName.toLowerCase().trim().replace(/\s+/g, '_');
    await setDoc(doc(db, 'playbookParents', parentId), {
      name: newParentName.trim(),
      priority: parents.length + 1
    });

    setNewParentName('');
  };

  const handleUpdateParentName = async (parentId) => {
    if (!editingParentName.trim()) return;
    await updateDoc(doc(db, 'playbookParents', parentId), {
      name: editingParentName.trim()
    });
    setEditingParentId(null);
  };

  const handleDeleteParent = async (parentId) => {
    // Reassign child folders to 'general' parent before deleting
    const childFolders = folders.filter((f) => f.parentFolder === parentId);
    for (const child of childFolders) {
      await updateDoc(doc(db, 'playbookFolders', child.id), { parentFolder: 'general' });
    }
    await deleteDoc(doc(db, 'playbookParents', parentId));
  };

  // --- SUB-FOLDER ACTIONS ---
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim() || !selectedParent) return;

    const folderId = newFolderName.toLowerCase().trim().replace(/\s+/g, '_');
    await setDoc(doc(db, 'playbookFolders', folderId), {
      name: newFolderName.trim(),
      parentFolder: selectedParent,
      priority: folders.length + 1,
      rules: []
    });

    setNewFolderName('');
  };

  const handleUpdateFolderName = async (folderId) => {
    if (!editingFolderName.trim()) return;
    await updateDoc(doc(db, 'playbookFolders', folderId), {
      name: editingFolderName.trim()
    });
    setEditingFolderId(null);
  };

  const handleSaveFolderRules = async (folderId) => {
    try {
      const rawText = folderTexts[folderId] || '';
      const rulesArray = rawText
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      await updateDoc(doc(db, 'playbookFolders', folderId), { rules: rulesArray });
      setOriginalFolderTexts((prev) => ({ ...prev, [folderId]: rawText }));
    } catch (err) {
      console.error(`Failed to save folder ${folderId}:`, err);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    await deleteDoc(doc(db, 'playbookFolders', folderId));
  };

  const handleMovePriority = async (folder, direction, parentId) => {
    // 1. Get ONLY the sub-folders belonging to this parent category, sorted by priority
    const siblingFolders = folders
      .filter((f) => (f.parentFolder || 'general') === parentId)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));

    const currentIndex = siblingFolders.findIndex((f) => f.id === folder.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Boundary check within the parent category
    if (targetIndex < 0 || targetIndex >= siblingFolders.length) return;

    // 2. Swap positions in the local array
    const reorderedSiblings = [...siblingFolders];
    const [movedFolder] = reorderedSiblings.splice(currentIndex, 1);
    reorderedSiblings.splice(targetIndex, 0, movedFolder);

    // 3. Batch re-index all siblings sequentially to permanently eliminate duplicates (e.g. duplicate #8s)
    try {
      const updatePromises = reorderedSiblings.map((f, index) => {
        const newPriority = (index + 1) * 1; // 1, 2, 3, 4...
        if (f.priority !== newPriority) {
          return updateDoc(doc(db, 'playbookFolders', f.id), { priority: newPriority });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Failed to update folder priorities:', err);
    }
  };

  // Optional: Auto-repair duplicate priorities across all folders
  const handleRepairAllPriorities = async () => {
    try {
      const sorted = [...folders].sort((a, b) => (a.priority || 0) - (b.priority || 0));
      const batchPromises = sorted.map((f, idx) =>
        updateDoc(doc(db, 'playbookFolders', f.id), { priority: idx + 1 })
      );
      await Promise.all(batchPromises);
      alert('All folder priorities normalized sequentially!');
    } catch (err) {
      console.error('Failed to normalize priorities:', err);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading Playbook Editor...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Navigation Header */}
      <div className="flex justify-between items-center border-b pb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">TOM Playbook Editor</h2>
          <p className="text-sm text-gray-500">Manage global instructions, parent categories, and know-how sub-folders.</p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg border">
          <button
            onClick={() => setActiveTab('global')}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
              activeTab === 'global' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Global Instructions
          </button>
          <button
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition ${
              activeTab === 'folders' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Structured Folders ({folders.length})
          </button>
        </div>
      </div>

      {/* TAB 1: GLOBAL INSTRUCTIONS */}
      {activeTab === 'global' && (
        <form onSubmit={handleSaveGlobal} className="space-y-4 bg-white p-6 border rounded-xl shadow-sm">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-800">Global Overriding Platform Rules</h3>
            {isGlobalDirty && (
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                ● Unsaved Changes
              </span>
            )}
          </div>
          <textarea
            rows={10}
            value={globalText}
            onChange={(e) => setGlobalText(e.target.value)}
            className="w-full p-4 border rounded-lg text-sm font-mono text-gray-800 leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!isGlobalDirty || isSavingGlobal}
              className={`px-6 py-2.5 font-semibold text-sm rounded-lg transition ${
                isGlobalDirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400'
              }`}
            >
              {isSavingGlobal ? 'Saving...' : isGlobalDirty ? 'Save Global Rules' : 'No Changes to Save'}
            </button>
          </div>
        </form>
      )}

      {/* TAB 2: STRUCTURED FOLDERS */}
      {activeTab === 'folders' && (
        <div className="space-y-6">
          {/* Create Parent Category Form */}
          <div className="bg-white p-5 border rounded-xl shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-gray-700 uppercase">Create Top-Level Parent Category</h3>
            <form onSubmit={handleCreateParent} className="flex gap-3">
              <input
                type="text"
                placeholder="New Parent Category Name (e.g. Licensing, Service Delivery)"
                value={newParentName}
                onChange={(e) => setNewParentName(e.target.value)}
                className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <button
                type="submit"
                className="px-5 py-2 bg-gray-900 hover:bg-black text-white font-semibold text-sm rounded-lg"
              >
                Add Parent Category
              </button>
            </form>
          </div>

          {/* Create Sub-Folder Form */}
          <div className="bg-white p-5 border rounded-xl shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-gray-700 uppercase">Create Know-How Sub-Folder</h3>
            <form onSubmit={handleCreateFolder} className="flex gap-3">
              <select
                value={selectedParent}
                onChange={(e) => setSelectedParent(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Sub-Folder Name (e.g. Registered Submissions)"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />

              <button
                type="submit"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg"
              >
                Add Sub-Folder
              </button>
            </form>
          </div>

          {/* Render Parent Categories & Sub-Folders */}
          {parents.map((parent) => {
            const parentSubFolders = folders.filter((f) => (f.parentFolder || 'general') === parent.id);

            return (
              <div key={parent.id} className="bg-gray-50 border rounded-xl p-5 space-y-4">
                {/* Parent Header */}
                <div className="flex justify-between items-center border-b pb-3">
                  {editingParentId === parent.id ? (
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={editingParentName}
                        onChange={(e) => setEditingParentName(e.target.value)}
                        className="px-3 py-1 border rounded text-sm font-bold text-gray-800"
                      />
                      <button
                        onClick={() => handleUpdateParentName(parent.id)}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded font-medium"
                      >
                        Save Name
                      </button>
                      <button
                        onClick={() => setEditingParentId(null)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900 uppercase">📂 {parent.name}</h3>
                      <button
                        onClick={() => {
                          setEditingParentId(parent.id);
                          setEditingParentName(parent.name);
                        }}
                        className="text-xs text-blue-600 hover:underline ml-2"
                      >
                        Edit Name
                      </button>
                    </div>
                  )}

                  {parent.id !== 'general' && (
                    <button
                      onClick={() => handleDeleteParent(parent.id)}
                      className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 bg-red-50 rounded"
                    >
                      Delete Parent Category
                    </button>
                  )}
                </div>

                {/* Sub-Folders List */}
                <div className="space-y-4">
                  {parentSubFolders.map((folder) => {
                    const isCollapsed = collapsedFolders[folder.id];
                    const isDirty = isFolderDirty(folder.id);

                    return (
                      <div key={folder.id} className="bg-white border rounded-lg shadow-sm overflow-hidden">
                        {/* Sub-Folder Bar */}
                        <div className="p-3.5 bg-white hover:bg-gray-50 flex justify-between items-center border-b">
                          <div className="flex items-center gap-3 flex-1">
                            <span 
                              onClick={() => toggleCollapse(folder.id)}
                              className="cursor-pointer font-bold text-gray-700 text-sm"
                            >
                              {isCollapsed ? '▶' : '▼'}
                            </span>

                            {editingFolderId === folder.id ? (
                              <div className="flex gap-2 items-center">
                                <input
                                  type="text"
                                  value={editingFolderName}
                                  onChange={(e) => setEditingFolderName(e.target.value)}
                                  className="px-2 py-1 border rounded text-xs font-bold"
                                />
                                <button
                                  onClick={() => handleUpdateFolderName(folder.id)}
                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded"
                                >
                                  Save Name
                                </button>
                                <button
                                  onClick={() => setEditingFolderId(null)}
                                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                  Priority #{folder.priority}
                                </span>
                                <span 
                                  onClick={() => toggleCollapse(folder.id)}
                                  className="text-sm font-bold text-gray-900 cursor-pointer"
                                >
                                  {folder.name}
                                </span>
                                <button
                                  onClick={() => {
                                    setEditingFolderId(folder.id);
                                    setEditingFolderName(folder.name);
                                  }}
                                  className="text-[11px] text-blue-600 hover:underline"
                                >
                                  Edit Name
                                </button>
                                {isDirty && (
                                  <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                    ● Unsaved Edits
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Reordering and Delete */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleMovePriority(folder, 'up', parent.id)}
                              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                              title="Move Up"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMovePriority(folder, 'down', parent.id)}
                              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                              title="Move Down"
                            >
                              ▼
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteFolder(folder.id)}
                              className="px-2.5 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded font-medium ml-2 transition"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {/* Textarea Rules Content */}
                        {!isCollapsed && (
                          <div className="p-4 space-y-3 bg-gray-50/50">
                            <textarea
                              rows={6}
                              value={folderTexts[folder.id] || ''}
                              onChange={(e) => setFolderTexts({ ...folderTexts, [folder.id]: e.target.value })}
                              className="w-full p-3 border rounded-lg text-xs font-mono text-gray-800 leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                            />
                            <div className="flex justify-end">
                              <button
                                onClick={() => handleSaveFolderRules(folder.id)}
                                disabled={!isDirty}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition ${
                                  isDirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400'
                                }`}
                              >
                                {isDirty ? `Save ${folder.name} Rules` : 'Saved'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}